import { useEffect, useState, useCallback } from 'react'
import { inject, observer, Provider } from 'mobx-react'
import { getSnapshot } from 'mobx-state-tree'
import { Form } from 'react-final-form'
import { FORM_ERROR } from 'final-form'
import dayjs from 'dayjs'
import _ from 'lodash'
import { API } from '../../../api'
import { Button, ButtonsBox, Spacer, Loading } from '../../../components'
import { placeholderSymbolReplace, reduceValidationError, withRouterProps } from '../../../utils'
import { TAX_TYPES, SECURITY_TYPES_TEXT } from '../../../constants'
import { GreyText } from '../styled'
import PreTaxForm, { preTaxSchema } from './forms/PreTaxForm'
import RothForm, { rothSchema } from './forms/RothForm'
import AfterTaxForm, { afterTaxSchema } from './forms/AfterTaxForm'
import { typeToInvestmentsAPI } from './investmentUtils'
import AddNewInvestmentState from './AddNewInvestmentState'

const AddBalance = props => {
  const { store, account, match, toggleAdd } = props
  const {
    account: { preTaxContribAllowed, rothContribAllowed, posttaxContribAllowed, type },
  } = account

  const [newInvestmentState] = useState(() =>
    AddNewInvestmentState.create(
      { status: 'loading', securitySearchStatus: 'done' },
      { store, account }
    )
  )

  const fetchSecurity = useCallback(
    async investmentId => {
      try {
        const { data } = await API.get(`security/${investmentId}`)
        newInvestmentState.setSecurity(data)
      } catch (err) {
        console.error(err)
      }
    },
    [newInvestmentState]
  )

  const fetchInitialState = useCallback(async () => {
    try {
      newInvestmentState.setStatus('loading')

      const { getInstitutionalAccount } = store
      await getInstitutionalAccount()

      newInvestmentState.setStatus('done')
    } catch (err) {
      console.error(err)
      newInvestmentState.setStatus('error')
    }
  }, [newInvestmentState, store])

  useEffect(() => {
    const { investmentId } = props

    if (!investmentId) return

    try {
      newInvestmentState.setStatus('loading')
      fetchSecurity(investmentId)
      fetchInitialState()
      newInvestmentState.setStatus('done')
    } catch (err) {
      console.error(err)
    }
  }, [account.account, fetchInitialState, fetchSecurity, newInvestmentState, props])

  const onCancel = () => toggleAdd(null)

  const handleSave = async ({ preTax, roth, afterTax, resetForm }) => {
    try {
      const { type, id } = match.params
      const apiURL = typeToInvestmentsAPI({ type, id })

      if (preTax) {
        const {
          account: { participantId },
        } = account

        const securitySnapshot = getSnapshot(newInvestmentState.security)
        const { securityType, securityName: tickerName } = securitySnapshot
        const { purchaseDate, quantity, totalCostBasis, totalValue, valuationMethod } = preTax

        await API.post(apiURL, {
          dateOfUserInfo: dayjs().format('YYYY-MM-DD'),
          personId: participantId,
          securities: securitySnapshot,
          securityType,
          taxType: TAX_TYPES.preTax,
          tickerName,
          purchaseDate,
          quantity,
          totalCostBasis: totalCostBasis || 0,
          totalValue,
          valuationMethod,
        })
      }

      if (roth) {
        const {
          account: { participantId },
        } = account

        const securitySnapshot = getSnapshot(newInvestmentState.security)
        const { securityType, securityName: tickerName } = securitySnapshot
        const { purchaseDate, quantity, totalCostBasis, totalValue, valuationMethod } = roth

        await API.post(apiURL, {
          dateOfUserInfo: dayjs().format('YYYY-MM-DD'),
          personId: participantId,
          securities: securitySnapshot,
          securityType,
          taxType: TAX_TYPES.roth,
          tickerName,
          purchaseDate,
          quantity,
          totalCostBasis: totalCostBasis || 0,
          totalValue,
          valuationMethod,
        })
      }

      if (afterTax) {
        const {
          account: { participantId },
        } = account

        const securitySnapshot = getSnapshot(newInvestmentState.security)
        const { securityType, securityName: tickerName } = securitySnapshot
        const { purchaseDate, quantity, totalCostBasis, totalValue, valuationMethod } = afterTax

        await API.post(apiURL, {
          dateOfUserInfo: dayjs().format('YYYY-MM-DD'),
          personId: participantId,
          securities: securitySnapshot,
          securityType,
          taxType: TAX_TYPES.afterTax,
          tickerName,
          purchaseDate,
          quantity,
          totalCostBasis: totalCostBasis || 0,
          totalValue,
          valuationMethod,
        })
      }

      await store.getAccounts()
      await account.getInvestments({ type, id })
      await account.getAvailableInvestments()

      if (resetForm) {
        const { type, id } = match.params
        newInvestmentState.setSecurity(null)
        await fetchInitialState({ type, id })
      } else {
        onCancel()
      }
    } catch (err) {
      console.error(err)
      return { [FORM_ERROR]: 'Oops! Something went wrong, please try again later' }
    }
  }

  const initialValues = () => ({
    preTax: null,
    roth: null,
    afterTax: null,
  })

  const validate = async values => {
    const preTax = values.preTax
      ? await preTaxSchema
          .validate(values.preTax, { abortEarly: false })
          .then(() => {})
          .catch(err => reduceValidationError(err))
      : undefined

    const roth = values.roth
      ? await rothSchema
          .validate(values.roth, { abortEarly: false })
          .then(() => {})
          .catch(err => reduceValidationError(err))
      : undefined

    const afterTax = values.afterTax
      ? await afterTaxSchema
          .validate(values.afterTax, { abortEarly: false })
          .then(() => {})
          .catch(err => reduceValidationError(err))
      : undefined

    return _.pickBy({ preTax, roth, afterTax }, _.identity)
  }

  return (
    <Provider newInvestment={newInvestmentState}>
      {newInvestmentState.status === 'loading' ? (
        <Loading />
      ) : (
        <div>
          <GreyText>{SECURITY_TYPES_TEXT[newInvestmentState.security?.securityType]}</GreyText>

          <Spacer space='10px' />

          {newInvestmentState.security?.ticker && (
            <div>
              <GreyText>{newInvestmentState.security?.ticker}</GreyText>
              <Spacer space='10px' />
            </div>
          )}

          <GreyText>{placeholderSymbolReplace(newInvestmentState.security?.securityName)}</GreyText>

          <Spacer space='20px' />

          <Form
            onSubmit={handleSave}
            validate={validate}
            initialValues={initialValues()}
            subscription={{ values: true, submitting: true, submitError: true }}
            render={({ values, handleSubmit, submitting }) => (
              <div>
                {(preTaxContribAllowed || values.preTax) && (
                  <PreTaxForm security={newInvestmentState.security} type={type} />
                )}

                {(rothContribAllowed || values.roth) && (
                  <RothForm security={newInvestmentState.security} type={type} />
                )}

                {(posttaxContribAllowed || values.afterTax) && (
                  <AfterTaxForm security={newInvestmentState.security} type={type} />
                )}

                <ButtonsBox>
                  <Button
                    label='Cancel'
                    onClick={onCancel}
                    disabled={submitting}
                    secondary
                    width='85px'
                  />
                  <Button
                    label='Save'
                    onClick={handleSubmit}
                    disabled={submitting}
                    primary
                    width='85px'
                  />
                </ButtonsBox>
              </div>
            )}
          />
        </div>
      )}
    </Provider>
  )
}

export default withRouterProps(inject('store', 'account')(observer(AddBalance)))
